import { useEffect, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { api, formatCurrencyEUR, formatDate, formatNumber } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import { sanitizedHtml } from "../lib/sanitize";

export default function PositionsPage() {
  const { locale, t } = useI18n();
  const [deposits, setDeposits] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/deposits/me")
      .then(({ data }) => setDeposits(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="positions-page">
        <div className="br-fade-up">
          <div className="br-overline">{t("pos.overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            {t("pos.heading_a")} <em>{t("pos.heading_em")}</em>{t("pos.heading_b")}
          </h1>
          <p
            className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]"
            {...sanitizedHtml(t("pos.intro"))}
          />
        </div>

        {error && <div className="mt-6 br-card p-4 text-[color:var(--br-red)]">{String(error)}</div>}

        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="positions-table-wrapper">
          <table className="w-full text-left border-collapse" data-testid="positions-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>{t("pos.col_deposit")}</Th>
                <Th>{t("pos.col_principal")}</Th>
                <Th>{t("pos.col_gross_rate")}</Th>
                <Th>{t("pos.col_fee")}</Th>
                <Th>{t("pos.col_net_rate")}</Th>
                <Th>{t("pos.col_start")}</Th>
                <Th>{t("pos.col_end")}</Th>
                <Th align="right">{t("pos.col_accrued")}</Th>
                <Th align="right">{t("pos.col_daily")}</Th>
                <Th align="right">{t("pos.col_maturity_value")}</Th>
              </tr>
            </thead>
            <tbody>
              {deposits.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]">
                    {t("pos.empty")}
                  </td>
                </tr>
              ) : (
                deposits.map((d) => (
                  <tr key={d.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`position-row-${d.id}`}>
                    <Td>
                      <div className="font-serif-display italic text-[1.05rem] text-[color:var(--br-parchment)]">
                        {d.label || `${t("pos.col_deposit")} ${d.id.slice(-6)}`}
                      </div>
                      {d.auto_reinvest && (
                        <span
                          className="inline-block mt-2 font-mono-fin text-[0.58rem] tracking-[0.24em] uppercase border px-2 py-1"
                          style={{ borderColor: "var(--br-bronze)", color: "var(--br-bronze)" }}
                        >
                          {t("pos.auto_reinvest_short")}
                        </span>
                      )}
                    </Td>
                    <Td className="font-mono-fin text-[color:var(--br-parchment)]">{formatCurrencyEUR(d.principal, locale)}</Td>
                    <Td className="font-mono-fin">{formatNumber(d.annual_rate_pct, 2, locale)}%</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatNumber(d.mgmt_fee_pct, 2, locale)}%</Td>
                    <Td className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{formatNumber(d.net_rate_pct, 2, locale)}%</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(d.start_date, locale)}</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(d.maturity_date, locale)}</Td>
                    <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze-soft)" }}>
                      + {formatCurrencyEUR(d.accrued_net, locale)}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)]">{formatCurrencyEUR(d.daily_net, locale)}</Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)]">{formatCurrencyEUR(d.total_at_maturity_net, locale)}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-5 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}
